import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, DownloadIcon, AlertTriangleIcon, ShieldCheckIcon, ArrowDownLeftIcon, ArrowUpRightIcon } from 'lucide-react';
import { formatNaira } from '../utils/format';
import { TopBar } from '../components/TopBar';
interface TransactionDetailProps {
  navigate: (screen: string) => void;
  transactionId?: string;
}
export function TransactionDetail({
  navigate,
  transactionId = '1'
}: TransactionDetailProps) {
  const transactions: Record<string, any> = {
    '1': {
      type: 'Contribution Payment',
      title: 'Future Scholar Fund',
      amount: -16666,
      status: 'completed',
      date: 'Mar 15, 2026',
      time: '2:34 PM',
      ref: 'TXN-2026031500001',
      desc: 'Monthly contribution for Future Scholar Fund plan',
      from: 'Skydd Wallet',
      to: 'Leadway Assurance Plc',
      icon: ShieldCheckIcon,
      iconBg: 'bg-gray-50',
      iconColor: 'text-[#0D3B3B]'
    },
    '2': {
      type: 'Wallet Funding',
      title: 'Via Paystack',
      amount: 50000,
      status: 'completed',
      date: 'Mar 14, 2026',
      time: '10:15 AM',
      ref: 'TXN-2026031400002',
      desc: 'Wallet funding via debit card',
      from: 'Paystack',
      to: 'Skydd Wallet',
      icon: ArrowDownLeftIcon,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600'
    },
    '3': {
      type: 'Claim Payout',
      title: 'TravelGuard',
      amount: 120000,
      status: 'completed',
      date: 'Mar 10, 2026',
      time: '09:45 AM',
      ref: 'CLM-2026031000003',
      desc: 'Approved claim payout for delayed baggage',
      from: 'AXA Mansard',
      to: 'Skydd Wallet',
      icon: ArrowDownLeftIcon,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600'
    },
    '4': {
      type: 'Bank Transfer',
      title: 'To GTBank',
      amount: -25000,
      status: 'completed',
      date: 'Mar 05, 2026',
      time: '4:20 PM',
      ref: 'TRF-2026030500004',
      desc: 'Transfer to external bank account',
      from: 'Skydd Wallet',
      to: 'GTBank - 0123456789',
      icon: ArrowUpRightIcon,
      iconBg: 'bg-gray-50',
      iconColor: 'text-[#0D3B3B]'
    }
  };
  const tx = transactions[transactionId] || transactions['1'];
  const isIncoming = tx.amount > 0;
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.95
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 300
  }} className="flex-1 overflow-y-auto pb-safe bg-slate-50 flex flex-col h-full" data-id="element-2749">
      <TopBar title="Transaction" showBack onBack={() => navigate('wallet')} data-id="element-2750" />

      <div className="px-5 pt-8 pb-6 flex-1 flex flex-col" data-id="element-2751">
        {/* Header */}
        <div className="text-center mb-8" data-id="element-2752">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-500 mx-auto mb-4 border-4 border-white shadow-lg shadow-emerald-500/20" data-id="element-2753">
            <CheckCircleIcon className="w-10 h-10" data-id="element-2754" />
          </div>

          <h1 className={`text-4xl font-bold mb-2 tracking-tight ${isIncoming ? 'text-emerald-600' : 'text-[#0D3B3B]'}`} data-id="element-2755">
            {isIncoming ? '+' : ''}
            {formatNaira(Math.abs(tx.amount))}
          </h1>

          <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-2756">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-2757"></div>{' '}
            Completed
          </div>
        </div>

        {/* Details Card */}
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 mb-8" data-id="element-2758">
          <div className="flex items-center gap-3 pb-5 border-b border-gray-50 mb-5" data-id="element-2759">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tx.iconBg} ${tx.iconColor}`} data-id="element-2760">
              <tx.icon className="w-5 h-5" data-id="element-2761" />
            </div>
            <div data-id="element-2762">
              <h3 className="font-bold text-[#0D3B3B]" data-id="element-2763">{tx.type}</h3>
              <p className="text-xs text-gray-500" data-id="element-2764">{tx.title}</p>
            </div>
          </div>

          <div className="space-y-4" data-id="element-2765">
            <div className="flex justify-between items-start" data-id="element-2766">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-2767">
                Date & Time
              </span>
              <div className="text-right" data-id="element-2768">
                <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-2769">{tx.date}</p>
                <p className="text-xs text-gray-500" data-id="element-2770">{tx.time}</p>
              </div>
            </div>

            <div className="flex justify-between items-start" data-id="element-2771">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-2772">
                Reference
              </span>
              <p className="text-sm font-mono font-bold text-[#0D3B3B]" data-id="element-2773">
                {tx.ref}
              </p>
            </div>

            <div className="flex justify-between items-start" data-id="element-2774">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider w-24" data-id="element-2775">
                Description
              </span>
              <p className="text-sm font-medium text-[#0D3B3B] text-right" data-id="element-2776">
                {tx.desc}
              </p>
            </div>

            <div className="pt-4 mt-4 border-t border-gray-50 border-dashed" data-id="element-2777">
              <div className="flex justify-between items-center mb-3" data-id="element-2778">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-2779">
                  From
                </span>
                <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-2780">{tx.from}</p>
              </div>
              <div className="flex justify-between items-center" data-id="element-2781">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-2782">
                  To
                </span>
                <p className="text-sm font-bold text-[#0D3B3B]" data-id="element-2783">{tx.to}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-auto space-y-4" data-id="element-2784">
          <button className="w-full py-4 rounded-xl border-2 border-gray-200 text-[#0D3B3B] font-bold text-sm hover:border-gray-300 hover:bg-gray-50 transition-colors active:scale-95 flex items-center justify-center gap-2" data-id="element-2785">
            <DownloadIcon className="w-4 h-4" data-id="element-2786" /> Download Receipt
          </button>

          <div className="text-center" data-id="element-2787">
            <button className="text-xs font-bold text-gray-400 hover:text-rose-500 transition-colors flex items-center justify-center gap-1 mx-auto" data-id="element-2788">
              <AlertTriangleIcon className="w-3 h-3" data-id="element-2789" /> Report an Issue
            </button>
          </div>
        </div>
      </div>
    </motion.div>;
}